import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '../lib/cn';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  /** Optional sticky footer (e.g. action buttons). */
  footer?: ReactNode;
  /** Extra classes on the panel — e.g. to widen it. */
  className?: string;
}

/**
 * Accessible dialog that adapts to the device: a bottom sheet that slides up on
 * phones (thumb-reachable, no scroll-to-discover) and a centered card on larger
 * screens. Closes on overlay click or Escape and locks background scroll.
 */
export function Modal({ open, onClose, title, description, children, footer, className }: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" role="dialog" aria-modal="true">
      {/* Overlay */}
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-ink/40 backdrop-blur-sm animate-fade-in"
      />

      {/* Panel */}
      <div
        className={cn(
          'relative z-10 flex max-h-[92dvh] w-full flex-col overflow-hidden bg-card shadow-pop',
          'rounded-t-card animate-slide-up',
          'sm:m-4 sm:max-w-lg sm:rounded-card sm:animate-pop-in',
          className,
        )}
      >
        {/* Grab handle (mobile sheet affordance) */}
        <div className="mx-auto mt-2.5 h-1 w-9 shrink-0 rounded-pill bg-line-strong sm:hidden" aria-hidden />

        {(title || description) && (
          <div className="flex items-start justify-between gap-4 px-5 pb-4 pt-3 sm:px-6 sm:pt-5">
            <div className="min-w-0">
              {title && <h2 className="font-display text-lg font-semibold text-ink">{title}</h2>}
              {description && <p className="mt-1 text-sm text-muted">{description}</p>}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="-mr-1.5 -mt-0.5 inline-flex size-9 shrink-0 items-center justify-center rounded-control text-muted transition-colors hover:bg-surface hover:text-ink"
            >
              <X className="size-5" aria-hidden />
            </button>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-5 pb-5 sm:px-6">{children}</div>

        {footer && (
          <div className="border-t border-line px-5 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:px-6">{footer}</div>
        )}
      </div>
    </div>,
    document.body,
  );
}
